import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { handleUnauthorized } from '../../TokenAuth/auth';

const initialState = {
    loyaltyOffers: [],
    status: 'idle',
    loading: false,
    error: null,
    pagination: {
        totalDocs: 0,
        totalPages: 1,
        page: 1,
        limit: 10,
    },
};

export const fetchLoyaltyOffers = createAsyncThunk(
    'loyaltyOffer/fetch',
    async ({ page, limit, search = '' } = {}, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem("astroVani-admin-token");
            let url = `${process.env.REACT_APP_BASEURL}/admin/loyaltyOffer`;
            const params = new URLSearchParams();
            if (page !== undefined) params.append('page', page);
            if (limit !== undefined) params.append('limit', limit);
            if (search) params.append('search', search);

            if (params.toString()) {
                url += `?${params.toString()}`;
            }

            const response = await fetch(url, {
                method: "GET",
                headers: {
                    Authorization: token,
                    "Content-Type": "application/json",
                },
            });

            if (response.status === 401) {  
                handleUnauthorized();
                throw new Error("Unauthorized: Please login again");
            }

            if (!response.ok) {
                throw new Error('Failed to fetch loyalty offers');
            }

            const data = await response.json();
            return data;
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const createLoyaltyOffer = createAsyncThunk(
    'loyaltyOffer/create',
    async (payload, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem("astroVani-admin-token");
            const response = await fetch(`${process.env.REACT_APP_BASEURL}/admin/loyaltyOffer`, {
                method: "POST",
                headers: {
                    Authorization: token,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(payload)
            });

            if (response.status === 401) {  
                handleUnauthorized();
                throw new Error("Unauthorized: Please login again");
            }

            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.message || 'Failed to create loyalty offer');
            }
            return data;
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const updateLoyaltyOffer = createAsyncThunk(
    'loyaltyOffer/update',
    async ({ id, payload }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem("astroVani-admin-token");
            const response = await fetch(`${process.env.REACT_APP_BASEURL}/admin/loyaltyOffer/${id}`, {
                method: "PATCH",
                headers: {
                    Authorization: token,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(payload)
            });

            if (response.status === 401) {  
                handleUnauthorized();
                throw new Error("Unauthorized: Please login again");
            }

            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.message || 'Failed to update loyalty offer');
            }
            return data;
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const deleteLoyaltyOffer = createAsyncThunk(
    'loyaltyOffer/delete',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem("astroVani-admin-token");
            const response = await fetch(`${process.env.REACT_APP_BASEURL}/admin/loyaltyOffer/${id}`, {
                method: "DELETE",
                headers: {
                    Authorization: token,
                    "Content-Type": "application/json",
                },
            });

            if (response.status === 401) {  
                handleUnauthorized();
                throw new Error("Unauthorized: Please login again");
            }

            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.message || 'Failed to delete loyalty offer');
            }
            return data;
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const toggleLoyaltyOfferStatus = createAsyncThunk(
    'loyaltyOffer/toggleStatus',
    async ({ id, isActive }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem("astroVani-admin-token");
            const response = await fetch(`${process.env.REACT_APP_BASEURL}/admin/loyaltyOffer/toggle-status/${id}`, {
                method: "PATCH",
                headers: {
                    Authorization: token,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ isActive })
            });

            if (response.status === 401) {  
                handleUnauthorized();
                throw new Error("Unauthorized: Please login again");
            }

            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.message || 'Failed to toggle status');
            }
            return data;
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

const loyaltyOfferSlice = createSlice({
    name: 'loyaltyOffer',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchLoyaltyOffers.pending, (state) => {
                state.status = 'loading';
                state.loading = true;
            })
            .addCase(fetchLoyaltyOffers.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.loading = false;
                state.loyaltyOffers = action.payload?.data || action.payload?.data?.data || [];
                state.pagination = action.payload?.paginationDetail || initialState.pagination;
            })
            .addCase(fetchLoyaltyOffers.rejected, (state, action) => {
                state.status = 'failed';
                state.loading = false;
                state.error = action.payload || action.error.message;
            });
    },
});

export default loyaltyOfferSlice.reducer;
